import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';

/** Maintenance-log statuses (V2.4) — forward-only: meldt → paagaar → utfort. */
export type MaintenanceStatus = 'meldt' | 'paagaar' | 'utfort';

/** A component the lag maintains (heis, port, vaskeri …). */
export interface ComponentView {
  id: string;
  name: string;
  location: string | null;
  supplierName: string | null;
  notes: string | null;
}

export interface ComponentInput {
  name: string;
  location?: string | null;
  supplierName?: string | null;
  notes?: string | null;
}

/** One work-done note on a log's timeline. */
export interface MaintenanceEntryView {
  id: string;
  atUtc: string;
  byUserId: string;
  byName: string;
  note: string;
  photoRefs: string[];
  aiDrafted: boolean;
}

/** A log row (list shape). */
export interface MaintenanceLogSummary {
  id: string;
  title: string;
  status: MaintenanceStatus;
  componentId: string | null;
  componentName: string | null;
  reportedAtUtc: string;
  completedAtUtc: string | null;
  sourceCaseId: string | null;
  entryCount: number;
  hasPhotos: boolean;
}

/** The full log with the entries timeline. */
export interface MaintenanceLogDetail {
  id: string;
  title: string;
  description: string;
  status: MaintenanceStatus;
  componentId: string | null;
  componentName: string | null;
  supplierName: string | null;
  reportedAtUtc: string;
  completedAtUtc: string | null;
  sourceCaseId: string | null;
  sourceTaskId: string | null;
  photoRefs: string[];
  entries: MaintenanceEntryView[];
}

export interface CreateLogInput {
  title: string;
  description: string;
  componentId?: string | null;
  sourceCaseId?: string | null;
  sourceTaskId?: string | null;
}

export interface LogFilter {
  status?: string;
  componentId?: string;
  from?: string;
  to?: string;
}

/** An AI-drafted condition note — always un-saved, labelled, for human editing. */
export interface AiConditionDraft {
  draft: string;
  aiDrafted: boolean;
}

/**
 * Typed client for `/api/maintenance` (V2.4) — styret/vaktmester only; residents see
 * their sak via M6 and never reach this surface.
 */
@Injectable({ providedIn: 'root' })
export class MaintenanceService {
  private readonly http = inject(HttpClient);
  private static readonly Base = '/api/maintenance';

  createLog(body: CreateLogInput): Observable<MaintenanceLogDetail> {
    return this.http.post<MaintenanceLogDetail>(`${MaintenanceService.Base}/logs`, body);
  }

  updateLog(id: string, body: { status?: string; componentId?: string | null; supplierName?: string | null }): Observable<MaintenanceLogDetail> {
    return this.http.put<MaintenanceLogDetail>(`${MaintenanceService.Base}/logs/${encodeURIComponent(id)}`, body);
  }

  appendEntry(logId: string, note: string, photoRefs: string[] = [], aiDrafted = false): Observable<MaintenanceEntryView> {
    return this.http.post<MaintenanceEntryView>(`${MaintenanceService.Base}/logs/${encodeURIComponent(logId)}/entries`, { note, photoRefs, aiDrafted });
  }

  createComponent(body: ComponentInput): Observable<ComponentView> {
    return this.http.post<ComponentView>(`${MaintenanceService.Base}/components`, body);
  }

  /** The AI-tilstandsnotat draft — returned un-saved; the human edits and saves it as an entry. */
  aiDraft(logId: string): Observable<AiConditionDraft> {
    return this.http.post<AiConditionDraft>(`${MaintenanceService.Base}/logs/${encodeURIComponent(logId)}/ai-draft`, {});
  }
}
